import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState } from 'react';
import { Package, TrendingUp } from 'lucide-react';

export const ContainerCalculator = () => {
  const [units, setUnits] = useState('240');
  const [unitPrice, setUnitPrice] = useState('');
  const [containerType, setContainerType] = useState('40ft');

  const containerCapacity = {
    '20ft': 120,
    '40ft': 240,
    '40ft-hc': 280
  };

  const calculate = () => {
    const unitsNum = parseInt(units) || 0;
    const priceNum = parseFloat(unitPrice) || 0;
    const capacity = containerCapacity[containerType as keyof typeof containerCapacity];
    
    const containers = Math.ceil(unitsNum / capacity);
    const totalValue = unitsNum * priceNum;
    const valuePerContainer = containers > 0 ? totalValue / containers : 0;

    return {
      units: unitsNum,
      containers,
      capacity,
      totalValue,
      valuePerContainer,
      utilizationRate: capacity > 0 ? ((unitsNum % capacity) / capacity * 100).toFixed(1) : '0'
    };
  };

  const stats = calculate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          B2B Container Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="units">Number of Units</Label>
            <Input
              id="units"
              type="number"
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              placeholder="240"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="unitPrice">Unit Price (€)</Label>
            <Input
              id="unitPrice"
              type="number"
              step="0.01"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="containerType">Container Type</Label>
            <Select value={containerType} onValueChange={setContainerType}>
              <SelectTrigger id="containerType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20ft">20ft (120 units)</SelectItem>
                <SelectItem value="40ft">40ft (240 units)</SelectItem>
                <SelectItem value="40ft-hc">40ft HC (280 units)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
          <div className="text-center p-4 bg-primary/10 border border-primary/20 rounded-lg">
            <div className="text-2xl font-bold text-primary">{stats.containers}</div>
            <div className="text-sm text-muted-foreground mt-1">Containers Needed</div>
          </div>

          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold">€{stats.totalValue.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground mt-1">Total Value</div>
          </div>

          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold">€{stats.valuePerContainer.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="text-sm text-muted-foreground mt-1">Per Container</div>
          </div>

          <div className="text-center p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400 flex items-center justify-center gap-1">
              <TrendingUp className="h-5 w-5" />
              {stats.utilizationRate}%
            </div>
            <div className="text-sm text-muted-foreground mt-1">Utilization</div>
          </div>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg text-sm space-y-1">
          <p className="font-medium">Container Details:</p>
          <p>• Standard capacity: {stats.capacity} units per {containerType} container</p>
          <p>• Units per container: {Math.floor(stats.units / stats.containers)} units</p>
          <p>• Last container utilization: {stats.utilizationRate}%</p>
        </div>
      </CardContent>
    </Card>
  );
};